import { type JSX, type ReactNode } from "react";
import {
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxRoot,
  ComboboxValue,
  useComboboxAnchor,
} from "ui";

import { MultiSelectChip } from "src/components/MultiSelectChip";
import { useComboboxPortalContainer } from "src/hooks/useComboboxPortalContainer";

export interface MultiSelectChipsItem {
  id: string;
  label: string;
}

export interface MultiSelectChipProps<T extends MultiSelectChipsItem> {
  item: T;
  index: number;
}

interface MultiSelectChipsComboboxProps<T extends MultiSelectChipsItem> {
  items: T[];
  value: string[];
  onChange: (ids: string[]) => void;
  placeholder: string;
  emptyText: string;
  disabledIds?: string[];
  renderItem?: (item: T) => ReactNode;
  Chip?: (props: MultiSelectChipProps<T>) => JSX.Element;
}

export function MultiSelectChipsCombobox<T extends MultiSelectChipsItem>(
  props: MultiSelectChipsComboboxProps<T>,
) {
  const anchor = useComboboxAnchor();
  const portal = useComboboxPortalContainer();
  const Chip = props.Chip ?? MultiSelectChip;
  // Keep the order of `props.value`: chips (e.g. attributes) can be ordered.
  const selected = props.value.flatMap((id) => {
    return props.items.filter((item) => item.id === id);
  });

  return (
    <ComboboxRoot
      items={props.items}
      multiple
      value={selected}
      onValueChange={(items: T[]) => {
        props.onChange(
          items.map((item) => {
            return item.id;
          }),
        );
      }}
      itemToStringLabel={(item: T) => item.label}
      isItemEqualToValue={(item: T, value: T) => item.id === value.id}
    >
      <ComboboxChips
        ref={(element) => {
          anchor.current = element;
          portal.attach(element);
        }}
      >
        <ComboboxValue>
          {(value: T[]) => {
            return value.map((item, index) => {
              return <Chip key={item.id} item={item} index={index} />;
            });
          }}
        </ComboboxValue>
        <ComboboxChipsInput
          placeholder={selected.length === 0 ? props.placeholder : undefined}
        />
      </ComboboxChips>
      <ComboboxContent anchor={anchor} container={portal.container}>
        <ComboboxEmpty>{props.emptyText}</ComboboxEmpty>
        <ComboboxList>
          {(item: T) => {
            return (
              <ComboboxItem
                key={item.id}
                value={item}
                disabled={props.disabledIds?.includes(item.id)}
              >
                {props.renderItem ? props.renderItem(item) : item.label}
              </ComboboxItem>
            );
          }}
        </ComboboxList>
      </ComboboxContent>
    </ComboboxRoot>
  );
}
